import {useState} from "react";
import {DialogBody, DialogFooter, Spinner, Switch} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {COLORS} from "../../../constants/constants";
import {millisecondsToDutation} from "../../../utils/General";
import {CancelButton} from "../../common/StyledButtons";
import StyledNumericInput from "../../common/StyledNumericInput";
import {
  computeRouteCost,
  formatCurrency,
  RateKey,
  RATE_PRECISION,
  roundRateForDisplay,
  RouteCostRates,
  RouteCostTotals
} from "../RouteCost.service";
import {StyledDialog} from "../styles/styleds";

/**
 * Roomier padding than the shared StyledDialog default. Extended locally so the other
 * route-planning dialogs (ChangeDriverDialog, MoveJobDialog) keep their own spacing.
 */
const CostDialog = styled(StyledDialog)`
  .bp5-dialog-body {
    padding: 4px 20px 20px;
  }

  .bp5-dialog-header {
    padding: 4px 8px 4px 20px;
  }

  .bp5-dialog-footer {
    padding: 4px 20px 16px;
  }
`;

const MILLIS_PER_SECOND = 1000;
/**
 * 3 decimal places. NOT cosmetic: Blueprint derives stepMaxPrecision from minorStepSize, and its
 * default of 0.1 would round every roundAndClampValue path (arrow keys, bounds change) to one
 * decimal — silently rewriting a per-mile rate of 0.655 to 0.7. Omitting it is worse still:
 * stepSize defaults to 1, giving zero decimals. Per-mile and fuel-surcharge rates are routinely
 * quoted to three decimals, so 0.001 is also the right domain precision.
 */
const RATE_STEP = 1 / 10 ** RATE_PRECISION;

type RowSpec = {
  key: RateKey;
  label: string;
  rateLabel: string;
  /** The quantity shown beneath the label. */
  quantity: (totals: RouteCostTotals) => string;
};

const ROW_SPECS: RowSpec[] = [
  {
    key: "perMile",
    label: "Total miles",
    rateLabel: "$ per mile",
    quantity: (totals) => `${totals.totalMiles} mi`
  },
  {
    key: "perHour",
    label: "Total transit time",
    rateLabel: "$ per hour",
    quantity: (totals) => millisecondsToDutation(totals.totalTransitSeconds * MILLIS_PER_SECOND)
  },
  {
    key: "perDriver",
    label: "Total drivers",
    rateLabel: "$ per driver",
    quantity: (totals) => String(totals.totalRoutes)
  },
  {
    key: "perDeliveryStop",
    label: "Total delivery stops",
    rateLabel: "$ per stop",
    quantity: (totals) => String(totals.totalDeliveryStops)
  },
  {
    key: "perPickupStop",
    label: "Total pickup stops",
    rateLabel: "$ per stop",
    quantity: (totals) => String(totals.totalPickupStops)
  }
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  totals: RouteCostTotals;
  rates: RouteCostRates;
  onRateChange: (key: RateKey, value: string) => void;
  /** True while the map is refetching routes after an edit — the quantities are mid-flight. */
  isRecalculating?: boolean;
  /** When provided (existing manifests in plan), enables the "Added Cost" toggle. */
  beforeTotals?: RouteCostTotals | null;
};

const computeAddedTotals = (totals: RouteCostTotals, beforeTotals: RouteCostTotals): RouteCostTotals => ({
  totalMiles: Math.max(0, Math.round((totals.totalMiles - beforeTotals.totalMiles) * 100) / 100),
  totalTransitSeconds: Math.max(0, totals.totalTransitSeconds - beforeTotals.totalTransitSeconds),
  totalRoutes: Math.max(0, totals.totalRoutes - beforeTotals.totalRoutes),
  totalDeliveryStops: Math.max(0, totals.totalDeliveryStops - beforeTotals.totalDeliveryStops),
  totalPickupStops: Math.max(0, totals.totalPickupStops - beforeTotals.totalPickupStops)
});

const RouteCostDialog = ({
  isOpen,
  onClose,
  totals,
  rates,
  onRateChange,
  isRecalculating = false,
  beforeTotals
}: Props) => {
  const hasRoutes = totals.totalRoutes > 0;
  const hasBeforeTotals = !!beforeTotals;
  const [showAddedCost, setShowAddedCost] = useState(false);

  const displayTotals = showAddedCost && beforeTotals ? computeAddedTotals(totals, beforeTotals) : totals;
  const costLabel = showAddedCost ? "Added cost" : "Total cost";

  return (
    <CostDialog isOpen={isOpen} onClose={onClose} title="Cost Calculator" style={{width: 500}}>
      <DialogBody>
        {isRecalculating && (
          <RecalcNote data-testid="route-planning-cost-recalculating">
            <Spinner size={13} />
            <span>Recalculating routes...</span>
          </RecalcNote>
        )}

        {!hasRoutes ? (
          <EmptyState data-testid="route-planning-cost-empty">No routes to price.</EmptyState>
        ) : (
          <>
            {hasBeforeTotals && (
              <CostModeToggle>
                <Switch
                  data-testid="route-planning-cost-mode-toggle"
                  checked={showAddedCost}
                  onChange={() => setShowAddedCost(!showAddedCost)}
                  label="Show added cost only"
                  alignIndicator="right"
                />
              </CostModeToggle>
            )}

            {ROW_SPECS.map((spec) => (
              <Row key={spec.key} data-testid={`route-planning-cost-row-${spec.key}`}>
                <Metric>
                  <MetricLabel>{showAddedCost ? spec.label.replace("Total", "Added") : spec.label}</MetricLabel>
                  <MetricValue data-testid={`route-planning-cost-qty-${spec.key}`}>
                    {spec.quantity(displayTotals)}
                  </MetricValue>
                </Metric>
                <RateLabel htmlFor={`route-planning-cost-input-${spec.key}`}>{spec.rateLabel}</RateLabel>
                <RateCell>
                  <StyledNumericInput
                    id={`route-planning-cost-input-${spec.key}`}
                    data-testid={`route-planning-cost-input-${spec.key}`}
                    value={rates[spec.key]}
                    placeholder="0.00"
                    min={0}
                    allowNegative={false}
                    minorStepSize={RATE_STEP}
                    // The SECOND argument is the raw typed string. Binding the first (parsed
                    // number) would echo "5." back as "5", eat the dot, and make "5.55" untypable.
                    onValueChange={(_valueAsNumber, valueAsString) => onRateChange(spec.key, valueAsString)}
                    // Settle the value once editing stops: at most 3 decimals, trailing cruft
                    // dropped. Free-typing stays untouched so a partial decimal is never fought.
                    onBlur={() => onRateChange(spec.key, roundRateForDisplay(rates[spec.key]))}
                  />
                </RateCell>
              </Row>
            ))}

            <TotalRule />
            <TotalRow>
              <TotalLabel>{costLabel}</TotalLabel>
              <TotalValue data-testid="route-planning-cost-total">
                {formatCurrency(computeRouteCost(displayTotals, rates))}
              </TotalValue>
            </TotalRow>
          </>
        )}
      </DialogBody>
      <DialogFooter
        actions={
          <CancelButton data-testid="route-planning-cost-close-btn" onClick={onClose}>
            Close
          </CancelButton>
        }
      />
    </CostDialog>
  );
};

export default RouteCostDialog;

const CostModeToggle = styled.div`
  padding-bottom: 4px;
  .bp5-switch {
    margin-bottom: 0;
  }
`;

const RecalcNote = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${COLORS.TEXT_GRAY};
  padding-bottom: 8px;
`;

const EmptyState = styled.div`
  padding: 32px 0 28px;
  text-align: center;
  font-size: 14px;
  color: ${COLORS.TEXT_GRAY};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  column-gap: 10px;
  padding: 13px 0;

  /* border-TOP on every row: a rule above "Total miles", one between each pair, and none
     hanging below the last. A border-bottom with :last-of-type would leave that trailing
     line, because TotalRow is also a div sibling and takes the :last-of-type match. */
  border-top: 1px solid ${COLORS.BG_GRAY_LIGHT};
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const MetricLabel = styled.span`
  font-size: 14px;
  color: ${COLORS.TEXT_BLACK};
`;

const MetricValue = styled.span`
  font-size: 13px;
  color: ${COLORS.TEXT_GRAY};
  font-variant-numeric: tabular-nums;
`;

const RateLabel = styled.label`
  font-size: 13px;
  color: ${COLORS.TEXT_GRAY_MEDIUM};
  white-space: nowrap;
  /* Row is align-items: center, so the label already lines up with the input box. */
`;

/**
 * Blueprint's NumericInput has no width of its own and stretches to its container, so the width
 * is pinned here and pushed down through the input-group to the input itself.
 */
const RateCell = styled.div`
  width: 100px;

  .bp5-numeric-input,
  .bp5-input-group {
    width: 100%;
  }

  input {
    width: 100%;
  }
`;

const TotalRule = styled.hr`
  border: none;
  border-top: 1px solid ${COLORS.COLOR_BORDER_DIVIDER};
  margin: 4px 0 0;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0 4px;
`;

const TotalLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.TEXT_BLACK};
`;

const TotalValue = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: ${COLORS.TEXT_BLACK};
  font-variant-numeric: tabular-nums;
`;
